import * as Chance from 'chance';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAppSelector } from '../../redux/root';

import moment from 'moment';
import {
  AlQuran,
  AlQuranFormData,
  Azkaar,
  AzkaarFormData,
  Campange,
  CampangeFormData,
  Content,
  Cotisation,
  CotisationFormData,
  Countable,
  Filter,
  Khassida,
  KhassidaFormData,
  Playlist,
  PlaylistFormData,
  Salatou,
  SalatouFormData,
  UserFormData,
  UserInfos
} from '../../types';
import { SWAL_DEFAULT_PROPS } from '../data';
import { uploadImage, uploadUserAvatarAsync } from './storage.api';
import {
  Conds,
  addAdminFunction,
  cotisationListener,
  countableListener,
  createAlQuranAsync,
  createAzkaarAsync,
  createCampangeAsync,
  createCotisationsync,
  createKhassidaAsync,
  createPlaylistAsync,
  createSalatouAsync,
  responseMessageContact,
  updateAlQuranAsync,
  updateAzkaarAsync,
  updateCampangeAsync,
  updateCgu,
  updateCotisationAsync,
  updateKhassidaAsync,
  updatePlaylistAsync,
  updateSalatouAsync,
  updateUserAsync
} from './user.api';

function useCreateUser<T>(
  createfunction: (data: T) => Promise<any>,
  cb: () => void,
  success: string,
  error: string
) {
  const [loading, setLoading] = useState(false);

  const onAdd = useCallback(
    async (data: T) => {
      setLoading(true);
      try {
        const res = (await createfunction(data)) as {
          data: { ok: boolean };
        };
        if (res?.data?.ok) {
          cb();
          Swal.fire({
            title: 'Succès',
            text: success,
            icon: 'success',
            ...SWAL_DEFAULT_PROPS
          });
        } else {
          if (res?.data?.ok === false) {
            Swal.fire({
              title: 'Erreur',
              text: 'Adresse email existe déjà',
              icon: 'error',
              ...SWAL_DEFAULT_PROPS
            });
          } else {
            Swal.fire({
              title: 'Erreur',
              text: 'Une erreur est survenue',
              icon: 'error',
              ...SWAL_DEFAULT_PROPS
            });
          }
        }
      } catch (ex) {
        console.log('error', ex);
        Swal.fire({
          icon: 'error',
          title: error,
          showConfirmButton: false,
          timer: 5000,
          ...SWAL_DEFAULT_PROPS
        });
      }
      setLoading(false);
    },
    [cb]
  );

  return {
    loading,
    onAdd
  };
}

export async function generateMatricule() {
  const chance = new Chance();
  return `${moment().format('YY')}+${chance.string({
    length: 5,
    numeric: true,
    alpha: true
  })}`;
}

export function useCreateAdmin(cb: () => void, item?: UserInfos, img?: File) {
  const [updating, setUpdating] = useState(false);
  const { onAdd, loading } = useCreateUser<UserFormData>(
    addAdminFunction,
    cb,
    'Utilisateur ajouté avec succès',
    'Une erreur est survenue'
  );

  const onUpdate = async (data: UserFormData) => {
    console.log('item', item, data);
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      const newData = { ...data };
      if (img) {
        newData.avatar = await uploadUserAvatarAsync(img, item.uid);
      }
      await updateUserAsync(item.uid, newData);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Utilisateur modifié avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating || loading,
    onAddAdmin: (data: UserFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}

export function useAddContent(cb: () => void) {
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: string, type: keyof Content) => {
    setUpdating(true);
    try {
      await updateCgu(data, type);
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAdd
  };
}

export function useResponseContact(cb: () => void) {
  const [loading, setLoading] = useState(false);

  const onResponse = useCallback(
    async (id: string, response: string) => {
      setLoading(true);
      try {
        await responseMessageContact(id, response);
        setTimeout(() => cb(), 400);
        setTimeout(() => {
          Swal.fire({
            icon: 'success',
            title: 'Réponse envoyé avec succés',
            showConfirmButton: false,
            timer: 5000,
            ...SWAL_DEFAULT_PROPS
          });
          setLoading(false);
        }, 800);
      } catch (ex) {
        console.log('error', ex);
        setTimeout(() => cb(), 400);
        setTimeout(() => {
          Swal.fire({
            icon: 'error',
            title: 'Réponse non envoyé',
            showConfirmButton: false,
            timer: 5000,
            ...SWAL_DEFAULT_PROPS
          });
          setLoading(false);
        }, 800);
      }
    },
    [cb]
  );

  return {
    loading,
    onResponse
  };
}

export function useLocationOrRedirect<T>(redirectPath?: string) {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: T };
  useEffect(() => {
    if (!state) {
      if (redirectPath) navigate(redirectPath, { replace: true });
      else navigate(-1);
    }
  }, [state]);

  return state;
}

export const useDeleteItem = (
  deleteFunction: (id: string) => Promise<any>,
  success: string,
  error: string
) => {
  const onDelete = async (id: string) => {
    Swal.fire({
      title: "T'es sure de continuer ?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          await deleteFunction(id);
          Swal.fire({
            icon: 'success',
            title: success,
            showConfirmButton: false,
            timer: 5000,
            ...SWAL_DEFAULT_PROPS
          });
        } catch (ex) {
          Swal.fire({
            icon: 'error',
            title: error,
            showConfirmButton: false,
            timer: 5000,
            ...SWAL_DEFAULT_PROPS
          });
        }
      },
      ...SWAL_DEFAULT_PROPS
    });
  };

  return onDelete;
};

export function useCreateKhassida(cb: () => void, item?: Khassida) {
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: KhassidaFormData) => {
    setUpdating(true);
    try {
      await createKhassidaAsync(data);
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };

  const onUpdate = async (data: KhassidaFormData) => {
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      await updateKhassidaAsync(item.uid, data);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Données modifiées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAddKhassida: (data: KhassidaFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}

export function useCreateAzkaar(cb: () => void, item?: Azkaar) {
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: AzkaarFormData) => {
    setUpdating(true);
    try {
      await createAzkaarAsync(data);
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };

  const onUpdate = async (data: AzkaarFormData) => {
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      await updateAzkaarAsync(item.uid, data);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Données modifiées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAddKhassida: (data: AzkaarFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}

export function useCreateCotisation(cb: () => void, item?: Cotisation) {
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: CotisationFormData) => {
    setUpdating(true);
    try {
      await createCotisationsync(data);
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };

  const onUpdate = async (data: CotisationFormData) => {
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      await updateCotisationAsync(item.uid, data);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Données modifiées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAddKhassida: (data: CotisationFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}
export function useUpdateUser(item: UserInfos, cb: (ok: boolean) => void) {
  const [loading, setLoading] = useState(false);
  const onUpdate = useCallback(async (data: UserFormData) => {
    setLoading(true);
    try {
      const getData = async (data: any) => {
        const updates: UserFormData = {};
        for (let [k, v] of Object.entries(data)) {
          if (v) {
            if (v instanceof File) {
              updates[k] = await uploadImage(v);
            } else if (typeof v === 'object') {
              updates[k] = await getData(v);
            } else {
              updates[k] = v;
            }
          }
        }
        return updates;
      };
      const updates = await getData(data);
      await updateUserAsync(item.uid, updates);
      cb(true);
    } catch (ex) {
      cb(false);
    }
    setLoading(false);
  }, []);

  return {
    onUpdate,
    loading
  };
}

export function useCreateAlQuran(cb: () => void, item?: AlQuran) {
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: AlQuranFormData) => {
    setUpdating(true);
    try {
      await createAlQuranAsync(data);
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };

  const onUpdate = async (data: AlQuranFormData) => {
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      await updateAlQuranAsync(item.uid, data);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Données modifiées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAddAlQuran: (data: AlQuranFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}

export function useCreatePlaylist(cb: () => void, item?: Playlist) {
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: PlaylistFormData) => {
    setUpdating(true);
    try {
      await createPlaylistAsync(data);
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };

  const onUpdate = async (data: PlaylistFormData) => {
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      await updatePlaylistAsync(item.uid, data);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Données modifiées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAddKhassida: (data: PlaylistFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}

export function useCreateSalatou(cb: () => void, item?: Salatou) {
  const salatous = useAppSelector((s) => s.salatous);
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: SalatouFormData) => {
    setUpdating(true);
    try {
      const filtred = salatous.filter((s) => s.active);
      data.active = true;
      await createSalatouAsync(data);
      for (let i of filtred) {
        updateSalatouAsync(i.uid, { active: false });
      }
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };

  const onUpdate = async (data: SalatouFormData) => {
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      await updateSalatouAsync(item.uid, data);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Données modifiées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAddSalatou: (data: SalatouFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}

export function useCreateCampagne(cb: () => void, item?: Campange) {
  const campanges = useAppSelector((s) => s.campanges);
  const [updating, setUpdating] = useState(false);

  const onAdd = async (data: CampangeFormData) => {
    setUpdating(true);
    try {
      const filtred = campanges.filter((s) => s.active);
      data.active = true;
      await createCampangeAsync(data);
      for (let i of filtred) {
        updateCampangeAsync(i.uid, { active: false });
      }
      cb();
      Swal.fire({
        title: 'Succès',
        text: 'Données ajoutées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };

  const onUpdate = async (data: CampangeFormData) => {
    if (!item) return;
    const wait = () => new Promise((r) => setTimeout(r, 1000));
    setUpdating(true);
    try {
      await updateCampangeAsync(item.uid, data);
      cb();
      await wait();
      Swal.fire({
        title: 'Succès',
        text: 'Données modifiées avec succès',
        icon: 'success',
        ...SWAL_DEFAULT_PROPS
      });
    } catch (ex) {
      console.log('error', ex);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue',
        showConfirmButton: false,
        timer: 5000,
        ...SWAL_DEFAULT_PROPS
      });
    }
    setUpdating(false);
  };
  return {
    loading: updating,
    onAddCampagne: (data: CampangeFormData) => {
      return item ? onUpdate(data) : onAdd(data);
    }
  };
}

export function useCountableListener(
  filter: Filter,
  startDate?: string,
  endDate?: string,
  conds?: Conds,
  campagne?: string
) {
  const [data, setData] = useState<Countable[]>([]);
  const [loading, setLoading] = useState(true);
  const listner = React.useRef<() => void>(null);
  useEffect(() => {
    listner.current = countableListener(
      (data, removeds) => {
        setData((old) => {
          const newState = old.filter((o) => !removeds.includes(o.uid));
          for (const i of data) {
            const index = newState.findIndex((o) => o.uid === i.uid);
            if (index === -1) {
              newState.push(i);
            } else {
              newState[index] = i;
            }
          }
          return newState.sort((a, b) => b.date.date - a.date.date);
        });
        setLoading(false);
      },
      filter,
      startDate,
      endDate,
      conds,
      campagne
    );
    return () => {
      if (listner.current) {
        listner.current();
        listner.current = null;
      }
      setData([]);
      setLoading(true);
    };
  }, [filter, startDate, endDate, campagne]);

  return {
    data,
    loading
  };
}

export function useCotisationListener(
  filter: Filter,
  startDate?: string,
  endDate?: string,
  conds?: Conds<Cotisation>
) {
  const [data, setData] = useState<Cotisation[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const listner = cotisationListener(
      (data, deleteds) => {
        setData((old) => {
          const newState = old.filter((f) => !deleteds.includes(f.uid));
          for (const i of data) {
            const index = newState.findIndex((o) => o.uid === i.uid);
            if (index === -1) {
              newState.push(i);
            } else {
              newState[index] = i;
            }
          }
          return newState.sort((a, b) => b.date.date - a.date.date);
        });
        setLoading(false);
      },
      filter,
      startDate,
      endDate,
      conds
    );
    return () => {
      listner();
      setData([]);
      setLoading(true);
    };
  }, [filter, startDate, endDate]);

  return {
    data,
    loading
  };
}

export function useCountableForUser(
  filter: Filter,
  userId: string,
  startDate?: string,
  endDate?: string,
  campagne?: string
) {
  const [count, setCount] = useState(0);
  const { data, loading } = useCountableListener(
    filter,
    startDate,
    endDate,
    {
      field: 'user',
      value: userId
    },
    campagne
  );
  useEffect(() => {
    setCount(data.reduce((acc, cur) => acc + cur.count, 0));
  }, [data, userId]);

  return {
    count,
    loading,
    data
  };
}

export type DashboardHome = {
  khassida: number;
  salatou: number;
  azkaar: number;
  alQuran: number;
};

export function useDashboardHome(
  filter: Filter,
  startDate?: string,
  endDate?: string,
  campagne?: string
) {
  const newData: DashboardHome = {
    khassida: 0,
    salatou: 0,
    azkaar: 0,
    alQuran: 0
  };
  const [dashboard, setDashboard] = useState(newData);
  const [allData, setAllData] = useState<Countable[]>([]);
  const { data, loading } = useCountableListener(
    filter,
    startDate,
    endDate,
    undefined,
    campagne
  );
  useEffect(() => {
    setDashboard(() => {
      const dashboard = { ...newData };
      for (let i of data) {
        if (i.azkaar) dashboard.azkaar += i.count;
        if (i.khassida) dashboard.khassida += i.count;
        if (i.alQuran) dashboard.alQuran += i.count;
        if (i.salatou) dashboard.salatou += i.count;
      }
      return dashboard;
    });
    setAllData(data);
  }, [data]);

  return {
    dashboard,
    loading,
    data: allData
  };
}

export function useDashboardUser(
  filter: Filter,
  userId: string,
  startDate?: string,
  endDate?: string,
  campagne?: string
) {
  const newData: DashboardHome = {
    khassida: 0,
    salatou: 0,
    azkaar: 0,
    alQuran: 0
  };
  const [dashboard, setDashboard] = useState(newData);
  const [allData, setAllData] = useState<Countable[]>([]);
  const { data, loading } = useCountableForUser(
    filter,
    userId,
    startDate,
    endDate,
    campagne
  );
  useEffect(() => {
    setDashboard(() => {
      const dashboard = { ...newData };
      for (let i of data) {
        if (i.azkaar) dashboard.azkaar += i.count;
        if (i.khassida) dashboard.khassida += i.count;
        if (i.alQuran) dashboard.alQuran += i.count;
        if (i.salatou) dashboard.salatou += i.count;
      }
      return dashboard;
    });
    setAllData(data);
  }, [data]);

  return {
    dashboard,
    loading,
    data: allData
  };
}
